import ky, { isHTTPError, type KyInstance } from "ky"

import {
  NO_ACCESS_TYPE,
  NoAccessResponseError,
} from "./errors/noAccessResponseError"
import { ResponseError } from "./errors/responseError"
import { parseJSON } from "./json"

const HTTP_STATUS_UNAUTHORIZED = 401
const VA_PATH = "api/va/"
const VA_REFRESH_TOKEN_PATH = "api/va/v1/auth/refresh-token/"
const VA_OBTAIN_TOKEN_PATH = "api/va/v1/auth/obtain-token/"

type Request = Pick<
  KyInstance,
  "get" | "post" | "put" | "patch" | "delete" | "query"
>

type ProblemDetails = {
  detail: string
  status: number
  title: string
  traceId: string
  type: string
  missing?: unknown
}

const toStrings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : []

const isDev = import.meta.env.MODE === "development"

const prefixUrl = isDev ? "" : "https://dovetail.puzzel.com"

const authenticationRequest = ky.extend({
  prefix: prefixUrl,
  credentials: "include",
})

const refreshSession = async (): Promise<void> => {
  await authenticationRequest.post("auth/refresh")
}

const obtainVaToken = async (): Promise<void> => {
  const obtained = await authenticationRequest.post(VA_OBTAIN_TOKEN_PATH, {
    throwHttpErrors: (status) => status !== HTTP_STATUS_UNAUTHORIZED,
  })
  if (obtained.status === HTTP_STATUS_UNAUTHORIZED) {
    await refreshSession()
    await authenticationRequest.post(VA_OBTAIN_TOKEN_PATH)
  }
}

const refreshVaSession = async (): Promise<void> => {
  const refreshed = await authenticationRequest.post(VA_REFRESH_TOKEN_PATH, {
    throwHttpErrors: false,
  })
  if (!refreshed.ok) {
    await obtainVaToken()
  }
}

const isVaUrl = (url: string): boolean =>
  new URL(url).pathname.startsWith(`/${VA_PATH}`)

export const request: Request = ky.extend({
  prefix: prefixUrl,
  credentials: "include",
  parseJson: parseJSON,
  hooks: {
    afterResponse: [
      async ({ request: originalRequest, response, retryCount }) => {
        // Only refresh on first 403, not on subsequent retries
        if (response.status === HTTP_STATUS_UNAUTHORIZED && retryCount === 0) {
          //TODO: this will run a lot if multiple items tries to make a request with an expired token
          await (isVaUrl(originalRequest.url)
            ? refreshVaSession()
            : refreshSession())

          return ky.retry({
            request: new Request(originalRequest),
            code: "TOKEN_REFRESHED",
          })
        }
        return undefined
      },
    ],
    beforeError: [
      ({ error }) => {
        if (error instanceof ResponseError) {
          return error
        }

        if (isHTTPError<ProblemDetails>(error)) {
          const { response, data: body } = error

          if (body != null && typeof body === "object") {
            const { detail, title, traceId, type } = body
            const init = {
              error: response.statusText,
              statusCode: response.status,
              message: `${response.statusText}: ${title} ${detail}`,
              detail,
              url: error.request.url,
              title,
              traceId,
              type,
            }

            if (type === NO_ACCESS_TYPE) {
              return new NoAccessResponseError({
                ...init,
                missing: toStrings(body.missing),
              })
            }

            return new ResponseError(init)
          }

          return new ResponseError({
            error: "Unknown Error",
            statusCode: response.status,
            // oxlint-disable-next-line typescript/prefer-nullish-coalescing if body is empty, it should show "Unknown Error"
            message: (typeof body === "string" && body) || "Unknown Error",
            title: "Unknown Error",
            detail: "Unknown Error",
            traceId: "",
            url: "",
            type: "Unknown",
          })
        }

        const { message, name } = error
        return new ResponseError({
          error: name,
          statusCode: 0,
          message: `${name}: ${message}`,
          title: name,
          detail: message,
          traceId: "",
          url: "",
          type: "fall through error",
        })
      },
    ],
  },
})
